/**
 * FormField Component
 *
 * Standardized form field wrapper with consistent error display,
 * label, and accessibility support. Ensures all form fields follow
 * the same UX patterns.
 *
 * Features:
 * - Consistent error message display
 * - Accessible label/error associations
 * - Visual error states
 * - Required field indicators
 * - Helper text support
 *
 * @example
 * ```tsx
 * <FormField
 *   label="Email Address"
 *   required
 *   error={errors.email}
 *   helperText="We'll never share your email"
 * >
 *   <Input
 *     type="email"
 *     value={email}
 *     onChange={(e) => setEmail(e.target.value)}
 *     error={!!errors.email}
 *     aria-invalid={!!errors.email}
 *     aria-describedby={errors.email ? 'email-error' : 'email-helper'}
 *   />
 * </FormField>
 * ```
 */

import * as React from 'react';
import {AlertCircle} from 'lucide-react';
import {cn} from '@/shared/utils/utils';
import {Label} from './Label';

interface FormFieldProps {
  /** Field label */
  label: string;
  /** HTML id for the input (required for accessibility) */
  id: string;
  /** Error message to display */
  error?: string;
  /** Helper text shown below input */
  helperText?: string;
  /** Whether field is required */
  required?: boolean;
  /** Child input element */
  children: React.ReactElement;
  /** Additional className */
  className?: string;
  /** Visually hide label (keep accessible) */
  visuallyHiddenLabel?: boolean;
}

/**
 * FormField provides consistent structure for form inputs
 */
export const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      label,
      id,
      error,
      helperText,
      required = false,
      children,
      className,
      visuallyHiddenLabel = false,
    },
    ref
  ) => {
    const errorId = `${id}-error`;
    const helperId = `${id}-helper`;
    const hasError = !!error;

    // Clone child to add id and aria attributes
    const existingProps = children.props as Record<string, unknown>;
    const inputProps: Record<string, unknown> = {
      ...existingProps,
      id,
    };

    if (hasError) {
      inputProps['aria-invalid'] = 'true';
      inputProps['aria-describedby'] = errorId;
    } else if (helperText) {
      inputProps['aria-describedby'] = helperId;
    }

    const inputWithProps = React.cloneElement(children, inputProps);

    return (
      <div ref={ref} className={cn('space-y-2', className)}>
        {/* Label */}
        <Label
          htmlFor={id}
          required={required}
          invalid={hasError}
          visuallyHidden={visuallyHiddenLabel}
          size="md"
        >
          {label}
        </Label>

        {/* Input */}
        {inputWithProps}

        {/* Error Message */}
        {hasError && (
          <div
            id={errorId}
            role="alert"
            aria-live="polite"
            className="flex items-start gap-1.5 text-sm text-destructive animate-fade-in"
          >
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
            <span className="text-pretty">{error}</span>
          </div>
        )}

        {/* Helper Text */}
        {helperText && !hasError && (
          <p
            id={helperId}
            className="text-xs sm:text-sm text-muted-foreground text-pretty"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

FormField.displayName = 'FormField';
